/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { PageIncomingPurchaseListResponse } from '../models/page-incoming-purchase-list-response';
import { PageOutgoingPurchaseListResponse } from '../models/page-outgoing-purchase-list-response';
import { Pageable } from '../models/pageable';
import { PagedModelDeliveryMethodPageResponse } from '../models/paged-model-delivery-method-page-response';
import { PagedModelPurchaseAdminResponseModel } from '../models/paged-model-purchase-admin-response-model';
import { PagedModelPurchaseItemAdminResponseModel } from '../models/paged-model-purchase-item-admin-response-model';
import { PurchaseAdminResponseModel } from '../models/purchase-admin-response-model';
import { PurchaseDto } from '../models/purchase-dto';
import { PurchaseItemAdminResponseModel } from '../models/purchase-item-admin-response-model';
import { PurchaseStatusUpdateRequest } from '../models/purchase-status-update-request';

/**
 * PurchaseLists resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class PurchaseListsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation updatePurchaseListStatus
   */
  static readonly UpdatePurchaseListStatusPath = '/purchase-lists/status';

  /**
   * Updates the status of purchase list.
   *
   * Updates the status of purchase list based on the provided id and a status.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updatePurchaseListStatus()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePurchaseListStatus$Response(
    params: {
      body: PurchaseStatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PurchaseListsService.UpdatePurchaseListStatusPath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates the status of purchase list.
   *
   * Updates the status of purchase list based on the provided id and a status.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updatePurchaseListStatus$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePurchaseListStatus(
    params: {
      body: PurchaseStatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updatePurchaseListStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation listAllPurchases
   */
  static readonly ListAllPurchasesPath = '/purchases';

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listAllPurchases()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllPurchases$Response(
    params: {
      pageable: Pageable;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelPurchaseAdminResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PurchaseListsService.ListAllPurchasesPath,
      'get'
    );
    if (params) {
      rb.query('pageable', params.pageable, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/hal+json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelPurchaseAdminResponseModel>;
        })
      );
  }

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listAllPurchases$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllPurchases(
    params: {
      pageable: Pageable;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PagedModelPurchaseAdminResponseModel> {
    return this.listAllPurchases$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagedModelPurchaseAdminResponseModel>) =>
          r.body as PagedModelPurchaseAdminResponseModel
      )
    );
  }

  /**
   * Path part for operation getPurchaseById
   */
  static readonly GetPurchaseByIdPath = '/purchases/{purchaseId}';

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPurchaseById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPurchaseById$Response(
    params: {
      purchaseId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PurchaseAdminResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PurchaseListsService.GetPurchaseByIdPath,
      'get'
    );
    if (params) {
      rb.path('purchaseId', params.purchaseId, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/hal+json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PurchaseAdminResponseModel>;
        })
      );
  }

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPurchaseById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPurchaseById(
    params: {
      purchaseId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PurchaseAdminResponseModel> {
    return this.getPurchaseById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PurchaseAdminResponseModel>) =>
          r.body as PurchaseAdminResponseModel
      )
    );
  }

  /**
   * Path part for operation listItemsOfPurchaseById
   */
  static readonly ListItemsOfPurchaseByIdPath = '/purchases/{purchaseId}/items';

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listItemsOfPurchaseById()` instead.
   *
   * This method doesn't expect any request body.
   */
  listItemsOfPurchaseById$Response(
    params: {
      purchaseId: string;
      pageable: Pageable;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelPurchaseItemAdminResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PurchaseListsService.ListItemsOfPurchaseByIdPath,
      'get'
    );
    if (params) {
      rb.path('purchaseId', params.purchaseId, {});
      rb.query('pageable', params.pageable, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/hal+json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelPurchaseItemAdminResponseModel>;
        })
      );
  }

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listItemsOfPurchaseById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listItemsOfPurchaseById(
    params: {
      purchaseId: string;
      pageable: Pageable;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PagedModelPurchaseItemAdminResponseModel> {
    return this.listItemsOfPurchaseById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagedModelPurchaseItemAdminResponseModel>) =>
          r.body as PagedModelPurchaseItemAdminResponseModel
      )
    );
  }

  /**
   * Path part for operation getPurchaseItemById
   */
  static readonly GetPurchaseItemByIdPath =
    '/purchases/{purchaseId}/items/{purchaseItemId}';

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPurchaseItemById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPurchaseItemById$Response(
    params: {
      purchaseId: string;
      purchaseItemId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PurchaseItemAdminResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PurchaseListsService.GetPurchaseItemByIdPath,
      'get'
    );
    if (params) {
      rb.path('purchaseId', params.purchaseId, {});
      rb.path('purchaseItemId', params.purchaseItemId, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/hal+json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PurchaseItemAdminResponseModel>;
        })
      );
  }

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPurchaseItemById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPurchaseItemById(
    params: {
      purchaseId: string;
      purchaseItemId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PurchaseItemAdminResponseModel> {
    return this.getPurchaseItemById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PurchaseItemAdminResponseModel>) =>
          r.body as PurchaseItemAdminResponseModel
      )
    );
  }

  /**
   * Path part for operation getPurchaseDetails
   */
  static readonly GetPurchaseDetailsPath = '/purchase-lists/{purchaseId}';

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPurchaseDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPurchaseDetails$Response(
    params: {
      purchaseId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PurchaseDto>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PurchaseListsService.GetPurchaseDetailsPath,
      'get'
    );
    if (params) {
      rb.path('purchaseId', params.purchaseId, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PurchaseDto>;
        })
      );
  }

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPurchaseDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPurchaseDetails(
    params: {
      purchaseId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PurchaseDto> {
    return this.getPurchaseDetails$Response(params, context).pipe(
      map((r: StrictHttpResponse<PurchaseDto>) => r.body as PurchaseDto)
    );
  }

  /**
   * Path part for operation getOutgoingPurchaseLists
   */
  static readonly GetOutgoingPurchaseListsPath = '/purchase-lists/outgoing';

  /**
   * Gets outgoing purchase lists.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOutgoingPurchaseLists()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOutgoingPurchaseLists$Response(
    params: {
      status: 'IN_PROGRESS' | 'CLOSED';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageOutgoingPurchaseListResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PurchaseListsService.GetOutgoingPurchaseListsPath,
      'get'
    );
    if (params) {
      rb.query('status', params.status, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageOutgoingPurchaseListResponse>;
        })
      );
  }

  /**
   * Gets outgoing purchase lists.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getOutgoingPurchaseLists$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOutgoingPurchaseLists(
    params: {
      status: 'IN_PROGRESS' | 'CLOSED';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageOutgoingPurchaseListResponse> {
    return this.getOutgoingPurchaseLists$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageOutgoingPurchaseListResponse>) =>
          r.body as PageOutgoingPurchaseListResponse
      )
    );
  }

  /**
   * Path part for operation getIncomingPurchaseLists
   */
  static readonly GetIncomingPurchaseListsPath = '/purchase-lists/incoming';

  /**
   * Gets incoming purchase lists.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getIncomingPurchaseLists()` instead.
   *
   * This method doesn't expect any request body.
   */
  getIncomingPurchaseLists$Response(
    params: {
      status: 'IN_PROGRESS' | 'CLOSED';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageIncomingPurchaseListResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PurchaseListsService.GetIncomingPurchaseListsPath,
      'get'
    );
    if (params) {
      rb.query('status', params.status, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageIncomingPurchaseListResponse>;
        })
      );
  }

  /**
   * Gets incoming purchase lists.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getIncomingPurchaseLists$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getIncomingPurchaseLists(
    params: {
      status: 'IN_PROGRESS' | 'CLOSED';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageIncomingPurchaseListResponse> {
    return this.getIncomingPurchaseLists$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageIncomingPurchaseListResponse>) =>
          r.body as PageIncomingPurchaseListResponse
      )
    );
  }

  /**
   * Path part for operation listAllPurchasesByMerchantId
   */
  static readonly ListAllPurchasesByMerchantIdPath =
    '/merchants/{merchantId}/purchases';

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listAllPurchasesByMerchantId()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllPurchasesByMerchantId$Response(
    params: {
      merchantId: string;
      pageable: Pageable;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelPurchaseAdminResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PurchaseListsService.ListAllPurchasesByMerchantIdPath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.query('pageable', params.pageable, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/hal+json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelPurchaseAdminResponseModel>;
        })
      );
  }

  /**
   * Gets purchase details.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listAllPurchasesByMerchantId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllPurchasesByMerchantId(
    params: {
      merchantId: string;
      pageable: Pageable;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PagedModelPurchaseAdminResponseModel> {
    return this.listAllPurchasesByMerchantId$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagedModelPurchaseAdminResponseModel>) =>
          r.body as PagedModelPurchaseAdminResponseModel
      )
    );
  }

  /**
   * Path part for operation listAllDeliveryMethodsByMerchantId
   */
  static readonly ListAllDeliveryMethodsByMerchantIdPath =
    '/merchants/{merchantId}/delivery/methods';

  /**
   * Gets delivery methods.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listAllDeliveryMethodsByMerchantId()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllDeliveryMethodsByMerchantId$Response(
    params: {
      merchantId: string;
      pageable: Pageable;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelDeliveryMethodPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PurchaseListsService.ListAllDeliveryMethodsByMerchantIdPath,
      'get'
    );
    if (params) {
      rb.path('merchantId', params.merchantId, {});
      rb.query('pageable', params.pageable, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/hal+json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelDeliveryMethodPageResponse>;
        })
      );
  }

  /**
   * Gets delivery methods.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listAllDeliveryMethodsByMerchantId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listAllDeliveryMethodsByMerchantId(
    params: {
      merchantId: string;
      pageable: Pageable;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PagedModelDeliveryMethodPageResponse> {
    return this.listAllDeliveryMethodsByMerchantId$Response(
      params,
      context
    ).pipe(
      map(
        (r: StrictHttpResponse<PagedModelDeliveryMethodPageResponse>) =>
          r.body as PagedModelDeliveryMethodPageResponse
      )
    );
  }
}
